import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Trophy, Calendar, Instagram, Facebook, Newspaper } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, MaskLine, fmtDate } from "../../lib/motion.jsx";
import { getSportOverview, getSocial, getPress } from "../../lib/api";
import { TikTokEmbed } from "../../components/TikTokEmbed";

const HERO = "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/lb9oi9lm_image00001%20%281%29.webp";
const IMGS = {
  futsal: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/2go6mpan_667769689_122200312976371548_4558226131540071981_n.webp",
  football: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/lb9oi9lm_image00001%20%281%29.webp",
};

const SPONSORS = [
  { name: "Bouraza Green Tea", img: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/c279ep2h_logo%20bouraza%20%282%29_page-0001.jpg" },
  { name: "Brussels Airport", img: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/486fzm22_6fe6cf9bc13861766928b53837bfb69102de1ba6.webp" },
  { name: "Deal Drive Motors", img: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/86b5vzab_b212ed75-65fe-4db5-a9d0-503368a8fb15.webp" },
  { name: "TFA Group", img: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/3ti6qwkt_TFA.webp" },
  { name: "Mabari Consulting", img: "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/z4tbecru_Naamloos%20document%20%2818%29_page-0001.webp" },
];

const SportCard = ({ sport, to, label, ov, t, lang, delay }) => {
  const next = ov?.next_match;
  const pos = ov?.standings?.find((s) => s.is_inkad)?.position;
  return (
    <Reveal delay={delay}>
      <Link to={to} data-testid={`fc-sport-card-${sport}`}
        className="group relative block overflow-hidden border border-white/10 bg-[#141414] hover:border-[#CCFF00] transition-colors">
        <div className="aspect-[16/8] overflow-hidden">
          <img src={IMGS[sport]} alt={label} style={{ objectPosition: sport === "football" ? "center 22%" : "center 65%" }} className="h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h3 className="font-anton text-4xl md:text-5xl uppercase">{label}</h3>
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/60">
                {next && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-[#CCFF00]" />
                    {t("nextMatch")}: {fmtDate(next.datetime, lang)} · {next.time}
                  </span>
                )}
                {pos && (
                  <span className="flex items-center gap-1.5">
                    <Trophy size={13} className="text-[#CCFF00]" /> {t("standings")}: #{pos}
                  </span>
                )}
              </div>
            </div>
            <ArrowUpRight size={32} className="shrink-0 text-[#CCFF00] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

export default function FcHome() {
  const { t, lang } = useLang();
  const [futsal, setFutsal] = useState(null);
  const [football, setFootball] = useState(null);
  const [press, setPress] = useState([]);
  const [social, setSocial] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 180]);

  useEffect(() => {
    getSportOverview("futsal").then(setFutsal);
    getSportOverview("football").then(setFootball);
    getPress().then(setPress);
    getSocial("fc-inkad").then(setSocial);
  }, []);

  return (
    <div className="fc-theme" data-testid="fc-home">
      {/* HERO */}
      <section className="relative h-[80vh] min-h-[540px] overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src={HERO} alt="FC Inkad" style={{ objectPosition: "center 25%" }} className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 to-transparent" />
        </motion.div>
        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-16">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">{t("fcHeroKicker")}</span>
          </div>
          <h1 className="font-anton text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.85]">
            <MaskLine delay={0.1}>{t("fcHero1")}</MaskLine>
            <MaskLine delay={0.22} className="text-[#CCFF00]">{t("fcHero2")}</MaskLine>
            <MaskLine delay={0.34}>{t("fcHero3")}</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-6 max-w-lg text-white/70">{t("fcHeroLead")}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-8">
            <Link to="/fc-inkad/inschrijven" data-testid="fc-hero-register"
              className="inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-7 py-3.5 font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
              {t("nav_inschrijven")} <ArrowUpRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Sponsor marquee */}
      <div className="border-y border-white/10 bg-[#0A0A0A] py-6">
        <Marquee speed={50} gradient={false}>
          {SPONSORS.map((s) => (
            <span key={s.name} className="mx-10 inline-flex h-28 items-center justify-center rounded-sm bg-white px-10 align-middle" title={s.name} data-testid="fc-marquee-sponsor">
              <img src={s.img} alt={s.name} className="h-[88px] w-auto max-w-[340px] object-contain" />
            </span>
          ))}
        </Marquee>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-16 space-y-20">
        {/* SPORT CHOICE */}
        <section data-testid="fc-sport-choice">
          <h2 className="font-anton text-3xl uppercase mb-8">{t("chooseSport")}</h2>
          <div className="grid gap-6 lg:grid-cols-2">
            <SportCard sport="futsal" to="/fc-inkad/futsal" label={t("nav_futsal")} ov={futsal} t={t} lang={lang} delay={0} />
            <SportCard sport="football" to="/fc-inkad/veldvoetbal" label={t("nav_veldvoetbal")} ov={football} t={t} lang={lang} delay={0.1} />
          </div>
        </section>

        {/* News — Het Nieuwsblad */}
        <section data-testid="fc-press-section">
          <h2 className="font-anton text-3xl uppercase mb-6">{t("clubNews")}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {press.slice(0, 6).map((n, i) => (
              <Reveal key={n.id} delay={i * 0.06}>
                <a href={n.url} target="_blank" rel="noreferrer" data-testid="fc-press-card"
                  className="group flex h-full items-start justify-between gap-4 border border-white/10 bg-[#141414] p-6 hover:border-[#CCFF00]/50 transition-colors">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#CCFF00]">
                      <Newspaper size={12} /> {n.source}
                      {n.date && <span className="text-white/40">· {fmtDate(n.date, lang)}</span>}
                    </div>
                    <h3 className="font-bold text-lg leading-snug transition-colors group-hover:text-[#CCFF00]">{n.title}</h3>
                  </div>
                  <ArrowUpRight size={18} className="shrink-0 text-white/30 transition-all group-hover:text-[#CCFF00] group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Social */}
        <section>
          <h2 className="font-anton text-3xl uppercase mb-6 flex items-center gap-3">{t("social")} <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" /></h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {social.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <a href={s.permalink || undefined} target={s.permalink ? "_blank" : undefined} rel="noreferrer"
                  className="group relative block overflow-hidden border border-white/10" data-testid="fc-social-card">
                  <div className="aspect-square overflow-hidden bg-[#141414]">
                    {s.image && <img src={s.image} alt={s.handle} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-4">
                    <div className="mb-1 flex items-center gap-2 text-xs text-[#CCFF00]">
                      {s.platform === "instagram" ? <Instagram size={13} /> : <Facebook size={13} />} {s.handle}
                      {s.date && <span className="text-white/40">· {fmtDate(s.date, lang)}</span>}
                    </div>
                    <p className="line-clamp-3 text-sm text-white/90">{s.text}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
          {!social[0]?.live && <p className="mt-4 text-xs text-white/30">* {t("social")} — placeholder. Automatische feed wordt gekoppeld zodra beschikbaar.</p>}
        </section>

        {/* TikTok */}
        <section data-testid="fc-tiktok-section">
          <h2 className="font-anton text-3xl uppercase mb-6">TikTok</h2>
          <Reveal>
            <div className="border border-white/10 bg-[#141414] p-6 md:p-8">
              <div className="bg-white p-2">
                <TikTokEmbed dark />
              </div>
              <div className="mt-6 text-center">
                <a href="https://www.tiktok.com/@inkad.diegem" target="_blank" rel="noreferrer" data-testid="fc-tiktok-follow"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-6 py-3 text-sm font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
                  {t("followTiktok")}
                </a>
              </div>
            </div>
          </Reveal>
        </section>

        {/* SPONSORS */}
        <section data-testid="fc-sponsors-section">
          <h2 className="font-anton text-3xl uppercase mb-6">{t("sponsors")}</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {SPONSORS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.05}>
                <div data-testid="fc-sponsor-card" title={s.name}
                  className="flex aspect-[3/2] items-center justify-center border border-white/10 bg-white p-5 transition-transform duration-200 hover:scale-105">
                  <img src={s.img} alt={s.name} className="max-h-full max-w-full object-contain" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Join */}
        <Reveal>
          <section className="border border-[#CCFF00]/30 bg-gradient-to-br from-[#141414] to-[#0A0A0A] p-10 text-center md:p-16" data-testid="fc-join">
            <h2 className="font-anton text-4xl md:text-6xl uppercase">{t("joinClub")}</h2>
            <p className="mx-auto mt-4 max-w-md text-white/60">{t("fcHeroLead")}</p>
            <Link to="/fc-inkad/inschrijven" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-8 py-4 font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
              {t("nav_inschrijven")} <ArrowUpRight size={18} />
            </Link>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
